import { useEffect, useState } from 'react'

import useApi from '@/hooks/useApi'
import useCompany from '@/hooks/useCompany'
import type { Company } from '@/types/dashboard-types'
import { DATA_SOURCE_VALUES, type DataSource } from '@/types/data-source'
import logger from '@/utils/logger'

const log = logger({ category: 'useCompaniesForSource' })

/**
 * One fetch per session, shared by every dropdown. Module scope rather than provider state
 * so the list survives components mounting and unmounting as insights are configured.
 */
let timescaleCompanies: Company[] | undefined
let inFlight: Promise<Company[]> | undefined

const loadTimescaleCompanies = async (api: ReturnType<typeof useApi>['api']): Promise<Company[]> => {
  if (timescaleCompanies) return timescaleCompanies
  inFlight ??= (async () => {
    const response = await api.get<Company[]>({
      path: `/company-list?skip=0&take=5000&dataSource=${DATA_SOURCE_VALUES.timescale}`,
      origin: 'useCompaniesForSource',
    })
    if (response.error) {
      log.error('could not load Timescale companies: %o', response)
      return []
    }
    // Mirrors CompanyProvider, which hides the retired "... Inactive" companies.
    return (response.data ?? []).filter((c) => !c.name.toLowerCase().includes('inactive'))
  })()
  timescaleCompanies = await inFlight
  inFlight = undefined
  return timescaleCompanies
}

/**
 * The company list belonging to a given back end.
 *
 * Fresh comes from CompanyProvider, which the whole app already shares. Timescale is fetched
 * on demand, so users without the data source permission never pay for a list they cannot
 * reach. Both back ends use Fresh company ids, so a selection carries across a switch.
 */
const useCompaniesForSource = (source: DataSource): { companies: Company[]; isLoading: boolean } => {
  const { companies, isLoading } = useCompany()
  const { api } = useApi()
  const [loaded, setLoaded] = useState<Company[] | undefined>(timescaleCompanies)

  const wantsTimescale = source === DATA_SOURCE_VALUES.timescale

  useEffect(() => {
    if (!wantsTimescale || !api || loaded) return
    let cancelled = false
    void loadTimescaleCompanies(api).then((result) => {
      if (!cancelled) setLoaded(result)
    })
    return () => {
      cancelled = true
    }
  }, [wantsTimescale, api, loaded])

  if (!wantsTimescale) return { companies, isLoading }
  return { companies: loaded ?? [], isLoading: loaded === undefined }
}

export default useCompaniesForSource
